import React, { useState } from "react";

const Num = ({ label, value, onChange, step = 1, min, max }) => (
  <label className="cidp-field">
    <span>{label}</span>
    <input
      type="number"
      value={value}
      step={step}
      min={min}
      max={max}
      onChange={(e) => onChange(parseFloat(e.target.value))}
      data-testid={`inp-${label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
    />
  </label>
);

// label + range slider + numeric entry on one row
const SliderNum = ({ label, value, onChange, step = 0.01, min = 0, max = 1 }) => {
  const id = label.replace(/[^a-z0-9]+/gi, "-").toLowerCase();
  return (
    <div className="cidp-slnum">
      <div className="cidp-slnum-top">
        <span>{label}</span>
        <input
          type="number"
          value={value}
          step={step}
          min={min}
          max={max}
          onChange={(e) => onChange(parseFloat(e.target.value))}
          data-testid={`num-${id}`}
        />
      </div>
      <input
        type="range"
        value={value}
        step={step}
        min={min}
        max={max}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        data-testid={`sld-${id}`}
      />
    </div>
  );
};

const Collapsible = ({ title, open, children, testid }) => {
  const [isOpen, setOpen] = useState(open);
  return (
    <div className="cidp-coll" data-testid={testid}>
      <button className="cidp-coll-head" onClick={() => setOpen((o) => !o)} data-testid={`${testid}-toggle`}>
        <span className={`cidp-coll-caret ${isOpen ? "open" : ""}`}>▸</span>
        {title}
      </button>
      {isOpen && <div className="cidp-coll-body">{children}</div>}
    </div>
  );
};

export default function ConfigPanel({ layer, meta, cfg, set, actions, calibCities = [] }) {
  const lu = meta.landuse;

  if (layer === "Input Data") {
    const ds = cfg.viz_dataset;
    return (
      <div>
        <div className="cidp-sb-title">Data visualization</div>
        <div className="cidp-radios" data-testid="viz-dataset">
          {["Population density", "Land use", "Existing chargers", "Demand indicators"].map((d) => (
            <label key={d}>
              <input type="radio" checked={ds === d} onChange={() => set({ viz_dataset: d })} />
              {d}
            </label>
          ))}
        </div>
        {ds === "Land use" && (
          <>
            <div className="cidp-section-head" data-testid="landuse-header">Land use:</div>
            <div className="cidp-radios" data-testid="viz-lu">
              {[{ val: "Dominant", color: null }, ...lu.list.map((k) => ({ val: lu.full[k], color: lu.colors[k] }))].map((o) => (
                <label key={o.val}>
                  <input type="radio" name="viz-lu" checked={cfg.viz_lu === o.val} onChange={() => set({ viz_lu: o.val })} />
                  <span className="cidp-sw" style={{ background: o.color || "linear-gradient(90deg,#7e57c2,#b0bec5,#e53935,#1e88e5,#43a047)" }} />
                  {o.val}
                </label>
              ))}
            </div>
          </>
        )}
        {ds === "Existing chargers" && (
          <div>
            <div className="cidp-sub">Charger type</div>
            <div className="cidp-radios" data-testid="viz-exist-type">
              {["Both", "Slow", "Fast"].map((t) => (
                <label key={t}>
                  <input type="radio" name="viz-exist-type" checked={cfg.viz_exist_type === t} onChange={() => set({ viz_exist_type: t })} />
                  {t}
                </label>
              ))}
            </div>
            <div className="cidp-sub">Grid</div>
            <div className="cidp-radios" data-testid="viz-exist-grid">
              {["Coarse", "Fine"].map((g) => (
                <label key={g}>
                  <input type="radio" name="viz-exist-grid" checked={cfg.viz_exist_grid === g} onChange={() => set({ viz_exist_grid: g })} />
                  {g}
                </label>
              ))}
            </div>
          </div>
        )}
        {ds === "Demand indicators" && (
          <>
            <div className="cidp-section-head" data-testid="categories-header">Categories:</div>
            <div className="cidp-radios" data-testid="viz-ind">
              {meta.ahp.leaves.filter((l) => !l.startsWith("D")).map((l) => (
                <label key={l}>
                  <input type="radio" name="viz-ind" checked={cfg.viz_ind === meta.ahp.labels[l]} onChange={() => set({ viz_ind: meta.ahp.labels[l] })} />
                  {meta.ahp.labels[l]}
                </label>
              ))}
            </div>
          </>
        )}
        <p className="cidp-cap">Hover any cell for its full numbers.</p>
      </div>
    );
  }

  if (layer === "Coarse Distribution") {
    const luWeights = (wKey) => lu.list.map((k) => (
      <Num key={wKey + k} label={lu.full[k]} value={cfg[wKey][k]} step={0.05} min={0} max={2}
           onChange={(v) => set({ [wKey]: { ...cfg[wKey], [k]: v } })} />
    ));
    return (
      <div>
        <Collapsible title="Demand prediction" open testid="coll-demand">
          <Num label="Horizon year" value={cfg.horizon_year} step={1} onChange={(v) => set({ horizon_year: v })} />
          <Num label="Target EV share (%)" value={cfg.target_ev_share} step={1} onChange={(v) => set({ target_ev_share: v })} />
          <Num label="Chargers per EV" value={cfg.chargers_per_ev} step={0.01} onChange={(v) => set({ chargers_per_ev: v })} />
          <Num label="Fast share (%)" value={cfg.fast_share} step={1} onChange={(v) => set({ fast_share: v })} />
          <Num label="Fast power (kW)" value={cfg.fast_power_kw} step={1} onChange={(v) => set({ fast_power_kw: v })} />
          <Num label="Slow power (kW)" value={cfg.slow_power_kw} step={1} onChange={(v) => set({ slow_power_kw: v })} />
        </Collapsible>
        <Collapsible title="Land-use weights (slow chargers)" open={false} testid="coll-lu-slow">
          {luWeights("lu_weights_slow")}
          <Num label="Distance focus (slow)" value={cfg.decay_slow} step={0.05} min={0} max={1} onChange={(v) => set({ decay_slow: v })} />
        </Collapsible>
        <Collapsible title="Land-use weights (fast chargers)" open={false} testid="coll-lu-fast">
          {luWeights("lu_weights_fast")}
          <Num label="Distance focus (fast)" value={cfg.decay_fast} step={0.05} min={0} max={1} onChange={(v) => set({ decay_fast: v })} />
        </Collapsible>
        <Collapsible title="Calibrate against a city" open={false} testid="coll-calibrate">
          {calibCities.length === 0 ? (
            <p className="cidp-cap">No cities have the data required for calibration yet.</p>
          ) : (
            <>
              <select value={cfg.calib_city || calibCities[0].name}
                      onChange={(e) => set({ calib_city: e.target.value })} data-testid="calib-city">
                {calibCities.map((c) => <option key={c.name} value={c.name}>{c.name.split(",")[0]}</option>)}
              </select>
              <button className="cidp-btn primary" style={{ marginTop: 8 }}
                      onClick={() => actions.calibrate(cfg.calib_city || calibCities[0].name)}
                      data-testid="btn-calibrate">▶ Calibrate weights</button>
              <p className="cidp-cap">Fits the land-use weights &amp; distance focus to the chosen city's existing chargers and overwrites the values above.</p>
            </>
          )}
        </Collapsible>
      </div>
    );
  }

  if (layer === "Electrical Grid Validation") {
    return (
      <div>
        <button className="cidp-btn primary" onClick={actions.validate} data-testid="btn-validate">▶ Validate</button>
        <div className="cidp-sb-title">Grid fitting</div>
        <Num label="Max fit iterations" value={cfg.max_iterations} step={1} min={1} max={25}
             onChange={(v) => set({ max_iterations: Math.max(1, Math.round(v || 1)) })} />
        <div className="cidp-warn" data-testid="grid-iter-warning">
          ⚠️ Setting this too high will distort the distribution to fit the existing electrical grid rather than the forecast demand.
        </div>
        <p className="cidp-cap">Maximum number of times the coarse distribution is recalculated to try to fit the existing grid. If the service is offline, feasibility and excess load are left blank.</p>
      </div>
    );
  }

  if (layer === "Prioritization Map") {
    const sections = [
      { key: "meso_slow", def: meta.ahp.slow, title: "Slow charger priority weights", testid: "coll-prio-slow" },
      { key: "meso_fast", def: meta.ahp.fast, title: "Fast charger priority weights", testid: "coll-prio-fast" },
    ];
    const normalize = (wKey) => {
      const w = cfg[wKey];
      const s = Object.values(w).reduce((a, b) => a + (b || 0), 0);
      if (s <= 0) return;
      const nw = {};
      Object.keys(w).forEach((k) => { nw[k] = Math.round((w[k] / s) * 1000) / 1000; });
      set({ [wKey]: nw });
    };
    const restore = (wKey, def) => set({ [wKey]: { ...def } });
    return (
      <div>
        <button className="cidp-btn primary" onClick={actions.calcMeso} data-testid="btn-calc-meso">▶ Calculate priority map</button>
        <div className="cidp-sub">Map shows</div>
        <div className="cidp-seg" data-testid="sel-fastslow">
          {["Slow", "Fast"].map((s) => (
            <button key={s} className={cfg.meso_sel === s ? "on" : ""} onClick={() => set({ meso_sel: s })}>{s}</button>
          ))}
        </div>
        {sections.map((sec, i) => (
          <Collapsible key={sec.key} title={sec.title} open={i === 0} testid={sec.testid}>
            <div className="cidp-row" style={{ marginTop: 0 }}>
              <button className="cidp-btn" style={{ margin: "4px 0" }} onClick={() => normalize(sec.key)}
                      data-testid={`${sec.testid}-normalize`}>Normalize to 1</button>
              <button className="cidp-btn" style={{ margin: "4px 0" }} onClick={() => restore(sec.key, sec.def)}
                      data-testid={`${sec.testid}-defaults`}>Defaults</button>
            </div>
            {Object.entries(meta.ahp.groups).map(([g, leaves]) => (
              <div key={g}>
                <div className="cidp-grp">{g}</div>
                {leaves.map((l) => (
                  <SliderNum key={l} label={meta.ahp.labels[l]} value={cfg[sec.key][l]} step={0.01} min={0} max={1}
                             onChange={(v) => set({ [sec.key]: { ...cfg[sec.key], [l]: v } })} />
                ))}
              </div>
            ))}
          </Collapsible>
        ))}
      </div>
    );
  }

  if (layer === "Fine Distribution") {
    return (
      <div>
        <button className="cidp-btn primary" onClick={actions.calcFine} data-testid="btn-calc-fine">▶ Calculate fine distribution</button>
        <div className="cidp-sub">Paint map by</div>
        <div className="cidp-radios" data-testid="fine-viz">
          {["Slow chargers", "Fast chargers", "Added load"].map((v) => (
            <label key={v}>
              <input type="radio" name="fine-viz" checked={cfg.fine_viz === v} onChange={() => set({ fine_viz: v })} />
              {v}
            </label>
          ))}
        </div>
        <p className="cidp-cap">Chargers are allocated using the priority score from the Prioritization tab. If it has not been calculated yet, it is computed first.</p>
      </div>
    );
  }
  return null;
}
